import Vue from "vue";
import axios from "axios";
import {
  Grade,
  Subject,
  SelectOptionKey,
  SelectOption,
  Tutor,
  Pagination
} from "resources/assets/types";
import isNil from "lodash/isNil";
import isString from "lodash/isString";
import TutorCard from "../../components/TutorCard.vue";
import TutorDetail from "../../components/TutorDetail.vue";
import SimilarTutors from "../../components/SimilarTutors.vue";
import debounce from "lodash/debounce";
import ListingPaginator from "../../components/ListingPaginator.vue";
import qs from "qs";

interface PersistedHistoryState {
  search: SearchState;
  tutor: number;
}

interface SearchState {
  query: string;
  zip_code: string;
  grade: null | number;
  subject: null | number;
  page: number;
}

interface SearchPayload {
  zip_code: string;
  sort: string;
  direction: string;
  query: string;
  grade: null | number;
  subject: null | number;
  page: number;
}

/**
 * Create an object that represents a valid search query.
 */
const searchStub = (...mergeWith: object[]): SearchState =>
  Object.assign(
    {},
    {
      query: "",
      zip_code: "",
      grade: null,
      subject: null,
      page: 1
    },
    ...mergeWith
  );

interface PaginationMetaData {
  last_page: number;
  current_page: number;
  total: number;
  per_page: number;
}

const toPagination = (meta: PaginationMetaData | null): Pagination | null => {
  if (meta === null) {
    return null;
  }

  return {
    totalPages: meta.last_page,
    currentPage: meta.current_page,
    totalRecords: meta.total,
    recordsPerPage: meta.per_page
  };
};

const parsedQueryString = (): SearchState =>
  (qs.parse((location.search || "").substr(1)) as unknown) as SearchState;

/**
 * Parse the querystring in the URL onload, saving it indefinitely.
 */
const initialQueryStringParams: object = parsedQueryString();

export const PageComponent = Vue.extend({
  props: {
    initialZipCode: {
      type: String,
      required: false
    }
  },

  async created() {
    await this.loadGradesAndSubjects();
    let tutorId = Number(/\d+$/.exec(location.pathname)?.[0]) || null;

    this.loadTutors();

    if (tutorId) {
      this.showTutorDetails(tutorId);
    } else {
      this.pushHistoryRecord("/tutors");
    }

    window.addEventListener("popstate", event => this.loadState(event.state));
  },

  watch: {
    // pendingSearch watchers
    ...Object.keys(searchStub()).reduce(
      (acc: object, key: string) =>
        Object.assign(acc, {
          [`pendingSearch.${key}`]() {
            if (this.searching) {
              return;
            }

            // when a search input/filter has been updated...
            // clear an existing tutor detail display
            // @ts-ignore-next-line
            this.tutor = null;

            let overrides = {};

            // if the search filters are modified, reset back to page 1.
            if (key !== "page") {
              overrides = { page: 1 };
            }

            // set the main `search` to be this updated `pendingSearch` object
            // @ts-ignore-next-line
            this.syncSearch(overrides);

            // perform a fresh search to guarantee results are accurate
            this.loadTutors();

            // update the URL via pushState/replaceState to reflect the change
            // @ts-ignore-next-line
            this.pushHistoryRecord("/tutors");
          }
        }),
      {}
    )
  },

  data() {
    return {
      tutor: null as null | Tutor,
      error: null as null | string,
      searching: false,
      tutors: [] as Tutor[],
      grades: [] as Grade[],
      subjects: [] as Subject[],
      search: searchStub(
        { zip_code: this.initialZipCode },
        initialQueryStringParams
      ),
      pendingSearch: searchStub(
        { zip_code: this.initialZipCode },
        initialQueryStringParams
      ),
      pagination: null as Pagination | null
    };
  },

  computed: {
    gradeOptions(): SelectOption[] {
      return this.grades.map(s => ({
        key: s.slug,
        value: s.slug,
        label: s.description
      }));
    },

    subjectOptions(): SelectOption[] {
      return this.subjects.map(s => ({
        key: s.slug,
        value: s.slug,
        label: s.description
      }));
    },

    searchParams(): SearchPayload {
      const params = {
        zip_code: this.search.zip_code,
        sort: "distance",
        direction: "asc",
        query: this.search.query,
        grade: this.search.grade,
        subject: this.search.subject,
        page: this.search.page
      };

      // clean up the params, removing null / empty values
      for (const [key, value] of Object.entries(params)) {
        if (isNil(value) || (isString(value) && !value.length)) {
          delete params[key as keyof SearchPayload];
        }
      }

      return params;
    }
  },

  methods: {
    /**
     * This method is called when a popstate event is fired by a history change.
     */
    loadState(state: PersistedHistoryState): void {
      // first, apply the saved search params to the //Vue instance. Note that
      // this directly modifies the `search` params, NOT the `pendingSearch`, so
      // watchers on `pendingSearch` that trigger new history pushState changes
      Vue.set(this, "search", state.search);

      // Next, check if a tutor id is set, and load the tutor if so.
      if (state.tutor) {
        this.loadTutor(state.tutor);
      } else {
        this.tutor = null;
      }

      // Finally, do a search refresh to make sure results are accurate.
      this.loadTutors();
      this.scrollToTopOfPage();
      this.pendingSearch = parsedQueryString();
    },

    syncSearch(overrides: object = {}): void {
      this.search = { ...this.pendingSearch, ...overrides };
    },

    goToPage(page: number): void {
      this.pendingSearch.page = page;
    },

    scrollToTopOfPage(): void {
      window.scrollTo(0, 0);
    },

    /**
     * Create a new history state record.
     *
     * If the current URL has no querystring, the URL will be REPLACED.
     *
     * @param url
     * @param additionalState additional state data to merge into the history state.
     *
     * @note additionalState should ONLY contain keys in the data object for this
     * Vue instance.
     */
    pushHistoryRecord(pathname: null | string = null, additionalState = {}) {
      pathname = pathname || location.pathname;

      const currentPathnameWithQueryString = `${window.location.pathname}${window.location.search}`;
      const pathnameWithQueryString = `${pathname}?${qs.stringify(
        this.searchParams
      )}`;
      const state = { search: this.search, tutor: null, ...additionalState };

      if (currentPathnameWithQueryString !== pathnameWithQueryString) {
        if (location.search.length) {
          history.pushState(state, "", pathnameWithQueryString);
        } else {
          history.replaceState(state, "", pathnameWithQueryString);
        }
      }

      this.scrollToTopOfPage();
    },

    reset(): void {
      this.pendingSearch = searchStub({ zip_code: this.initialZipCode });
    },

    updateQuery: debounce(function(this: any, value: string) {
      this.pendingSearch.query = value.trim();
    }, 300),

    updateZipCode(value: string, validity: ValidityState) {
      if (!validity.valid) {
        return;
      }

      this.pendingSearch.zip_code = value;
    },
    updateGrade(key: SelectOptionKey) {
      this.pendingSearch.grade = key as number;
    },

    updateSubject(key: SelectOptionKey) {
      this.pendingSearch.subject = key as number;
    },

    async showTutorDetails(id: number) {
      this.pushHistoryRecord(`/tutors/${id}`, { tutor: id });
      await this.loadTutor(id);
    },

    backToResults() {
      this.tutor = null;
      this.pushHistoryRecord("/tutors");
    },

    async loadGradesAndSubjects() {
      const [grades, subjects] = await Promise.all([
        axios.get("/ajax/grades"),
        axios.get("/ajax/subjects")
      ]);

      this.grades = grades?.data?.data || [];
      this.subjects = subjects?.data?.data || [];
    },

    async loadTutors() {
      if (this.searching) {
        return;
      }

      this.error = null;
      this.tutors = [];
      this.searching = true;

      try {
        const response = await axios.get(`/ajax/tutors`, {
          params: this.searchParams
        });

        this.tutors = response?.data?.data || [];
        this.pagination = toPagination(response?.data?.meta);
      } catch (error) {
        let message = "There was a problem performing that search.";

        if (Number(error?.response?.status) === 422) {
          const fieldError: string | undefined = Object.values<string[]>(
            error?.response?.data?.errors || {}
          )?.[0]?.[0];

          if (fieldError?.length) {
            message = fieldError;
          }
        }

        this.error = message;
      } finally {
        this.searching = false;
      }
    },

    async loadTutor(id: number) {
      const tutor = this.tutors.find(tutor => tutor.id === id);

      if (tutor) {
        // fetch the tutor right from recent results, avoiding an HTTP round trip
        this.tutor = tutor;
      } else {
        const response = await axios.get(`/ajax/tutors/${id}`);

        this.tutor = response.data.data;
      }
    }
  },

  components: {
    TutorCard,
    TutorDetail,
    SimilarTutors,
    ListingPaginator
  }
});
